/* Componentes básicos do design system Modernist — mesmo vocabulário do
   Sistema Fiscal (Button, Card, Field, Input, Tag) + Modal para as "abas"
   flutuantes que este app usa em todas as telas. */
import React from "react";

export const Button = ({ variante = "primary", className = "", ...props }) => (
    <button type="button" className={`btn btn-${variante} ${className}`} {...props} />
);

export const Card = ({ as: As = "div", className = "", ...props }) => (
    <As className={`card ${className}`} {...props} />
);

export const Field = ({ label, children, className = "" }) => (
    <div className={`field ${className}`}>
        <label>
            {label}
            {children}
        </label>
    </div>
);

export const Input = ({ className = "", ...props }) => (
    <input className={`input ${className}`} {...props} />
);

export const Select = ({ className = "", children, ...props }) => (
    <select className={`input ${className}`} {...props}>{children}</select>
);

export const Textarea = ({ className = "", ...props }) => (
    <textarea className={`input ${className}`} {...props} />
);

export const Tag = ({ tom = "neutral", children }) => (
    <span className={`tag tag-${tom}`}>{children}</span>
);

/* ----------------------------------------------------------------------
   Datas
   A API entrega datas como texto ("2026-07-26", "2026-07-24 09:30:00"),
   nunca em UTC — ver dateStrings em BACK-END/config/database.js. Formatar
   com `new Date(...)` reintroduziria o fuso: new Date("2026-07-26") é lido
   como meia-noite UTC e, no Brasil, exibiria dia 25. Por isso as funções
   abaixo trabalham só com recorte de string.
   ---------------------------------------------------------------------- */

/** "2026-07-26" ou "2026-07-26 09:30:00" → "26/07/2026" */
export const dataBR = (valor) => {
    if (!valor) return "";
    const [a, m, d] = String(valor).split(/[ T]/)[0].split("-");
    return d ? `${d}/${m}/${a}` : String(valor);
};

/** → "26/07/2026 09:30" */
export const dataHoraBR = (valor) => {
    if (!valor) return "";
    const [data, hora] = String(valor).split(/[ T]/);
    return `${dataBR(data)}${hora ? " " + hora.slice(0, 5) : ""}`;
};

/** → "2026-07-26" (valor de <input type="date">) */
export const paraInputData = (valor) => (valor ? String(valor).split(/[ T]/)[0] : "");

/** → "2026-07-26T09:30" (valor de <input type="datetime-local">) */
export const paraInputDataHora = (valor) => {
    if (!valor) return "";
    const [data, hora] = String(valor).split(/[ T]/);
    return `${data}T${(hora || "00:00").slice(0, 5)}`;
};

/** Dias corridos entre hoje e uma data (positivo = futuro).
    Use em compromissos e pagamentos (dias corridos).
    PRAZOS PROCESSUAIS contam em DIAS ÚTEIS (CPC art. 219): esse cálculo é
    feito no servidor, que depende de feriados nacionais/estaduais (API) e dos
    manuais do escritório. As telas leem o número pronto do back — `dias_uteis`
    em /prazos e `dias` em /dashboard — em vez de recalcular feriados aqui. */
export const diasAte = (valor) => {
    if (!valor) return 0;
    const [a, m, d] = String(valor).split(/[ T]/)[0].split("-").map(Number);
    const alvo = new Date(a, m - 1, d);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return Math.round((alvo - hoje) / 86400000);
};

/** Valor monetário em reais */
export const brl = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/* ----------------------------------------------------------------------
   Número de processo CNJ (Resolução 65/2008)
   O back-end é a autoridade que valida (utils/validators.js). Aqui a
   verificação é só para dar feedback imediato ao digitar.
   ---------------------------------------------------------------------- */

/** Aplica a máscara NNNNNNN-DD.AAAA.J.TR.OOOO conforme se digita */
export const mascaraCNJ = (valor) => {
    const d = String(valor || "").replace(/\D/g, "").slice(0, 20);
    let saida = d.slice(0, 7);
    if (d.length > 7) saida += "-" + d.slice(7, 9);
    if (d.length > 9) saida += "." + d.slice(9, 13);
    if (d.length > 13) saida += "." + d.slice(13, 14);
    if (d.length > 14) saida += "." + d.slice(14, 16);
    if (d.length > 16) saida += "." + d.slice(16, 20);
    return saida;
};

/** Confere o dígito verificador do número CNJ (mesmo cálculo do back) */
export const cnjValido = (valor) => {
    const d = String(valor || "").replace(/\D/g, "");
    if (d.length !== 20) return false;
    const base = d.slice(0, 7) + d.slice(9, 20) + "00";
    let resto = 0;
    for (const c of base) resto = (resto * 10 + Number(c)) % 97;
    return String(98 - resto).padStart(2, "0") === d.slice(7, 9);
};

/** Tom da tag por status — semântica compartilhada entre as telas */
export const tomDoStatus = (status) => {
    switch ((status || "").toLowerCase()) {
        case "ganho":
        case "concluido":
        case "comparecido":
            return "success";
        case "em andamento":
        case "pendente":
        case "adiado":
        case "remarcado":
        case "parcelado":
            return "warn";
        case "perdido":
        case "cancelado":
        case "em atraso":
            return "danger";
        default:
            return "neutral";
    }
};

/** Aba flutuante padrão: overlay escurecido + card elevado centralizado */
export const Modal = ({ titulo, onFechar, largo = false, children }) => (
    <div
        className="modal-overlay"
        onMouseDown={(e) => { if (e.target === e.currentTarget && onFechar) onFechar(); }}
    >
        <div className={`modal ${largo ? "modal-wide" : ""}`} role="dialog" aria-label={titulo}>
            <div className="modal-header">
                <h3>{titulo}</h3>
                {onFechar && (
                    <Button variante="ghost" className="btn-icon fechar" aria-label="Fechar" onClick={onFechar}>
                        ✕
                    </Button>
                )}
            </div>
            {children}
        </div>
    </div>
);
